import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type SyntheticEvent } from 'react';
import { api, errorMessage } from '../../api/client';
import { CopyField } from '../../components/CopyField';

export const CLOUD_KEYS_KEY = ['cloud-keys'] as const;
const RUNPOD_KEYS_PAGE = 'https://console.runpod.io/user/settings';

/** Opt-in: the key on Couch, after the user confirmed what it can do (doc 55). */
function KeepOnCouch({ onDone }: { onDone: () => void }) {
  const queryClient = useQueryClient();
  const [key, setKey] = useState('');
  const [understood, setUnderstood] = useState(false);
  const save = useMutation({
    mutationFn: () => api.setRunPodKey(key.trim()),
    onSuccess: async () => { setKey(''); onDone(); await queryClient.invalidateQueries({ queryKey: CLOUD_KEYS_KEY }); },
  });
  const submit = (e: SyntheticEvent) => { e.preventDefault(); save.mutate(); };
  return (
    <form className="runpod-key-form" onSubmit={submit}>
      <label htmlFor="runpod-key">RunPod API key</label>
      <input id="runpod-key" type="password" autoComplete="off" spellCheck={false} value={key} onChange={(e) => { setKey(e.target.value); }} />
      <label className="checkbox">
        <input type="checkbox" checked={understood} onChange={(e) => { setUnderstood(e.target.checked); }} />
        I understand: kept encrypted on Couch, this key could reach my whole RunPod account if Couch were broken into.
      </label>
      {save.isError && <p role="alert" className="form-error">{errorMessage(save.error)}</p>}
      <button type="submit" disabled={!understood || key.trim().length < 8 || save.isPending}>Save on Couch</button>
    </form>
  );
}

/** Where the user's RunPod key for Couch-managed pods is — never the key itself (doc 55). */
export function RunPodKeyPanel() {
  const queryClient = useQueryClient();
  const [opting, setOpting] = useState(false);
  const keys = useQuery({ queryKey: CLOUD_KEYS_KEY, queryFn: api.cloudKeys });
  const remove = useMutation({ mutationFn: api.removeRunPodKey, onSuccess: () => queryClient.invalidateQueries({ queryKey: CLOUD_KEYS_KEY }) });
  const holders = keys.data?.runpod;
  return (
    <div className="runpod-key">
      <h3>Your RunPod key for Couch-managed pods</h3>
      <p>
        With a key, Couch can create, start, stop and delete pods for you. A RunPod key for pods can do <strong>everything in your RunPod
        account</strong> — keep the prepaid credit small. Create one under <a href={RUNPOD_KEYS_PAGE} target="_blank" rel="noopener noreferrer">RunPod API keys ↗</a>.
      </p>
      {keys.isError && <p role="alert" className="form-error">{errorMessage(keys.error)}</p>}
      {holders && holders.machines.map((m) => <p key={m.id} className="success">{m.name} holds a key{m.online ? '' : ' (offline)'}.</p>)}
      <h4>On a home machine (recommended — Couch never sees the key)</h4>
      <p>In a terminal on that machine (the key is asked for hidden and kept in its keychain):</p>
      <CopyField value="~/Couch/venv/bin/couch-agent cloud-key set runpod" label="Keep the key on this Mac or Linux machine" />
      <CopyField value={'%USERPROFILE%\\Couch\\venv\\Scripts\\couch-agent.exe cloud-key set runpod'} label="Keep the key on this Windows PC" />
      <h4>On Couch (opt-in)</h4>
      {holders?.server.set ? (
        <p>
          A key is kept on Couch, encrypted.{' '}
          <button type="button" className="button-quiet" disabled={remove.isPending} onClick={() => { remove.mutate(); }}>Remove the key from Couch</button>
        </p>
      ) : opting ? (
        <KeepOnCouch onDone={() => { setOpting(false); }} />
      ) : (
        <p>
          Then Couch can also start a pod for a waiting run and enforce your monthly limit when no home machine is on.{' '}
          <button type="button" className="button-quiet" onClick={() => { setOpting(true); }}>Keep my key on Couch…</button>
        </p>
      )}
    </div>
  );
}
